import React, { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";
import { toast } from "sonner";
import { updateWaitlistPreferences, getWaitlistEntry } from "../lib/api";

/**
 * FYLLR — Preferences (a.k.a. "Personalise your picks").
 *
 * Reached via a link inside the post-signup Klaviyo email:
 *   https://fyllr.app/preferences?email=<url-encoded email>
 *
 * Collects the personalisation moved off the signup screen: region,
 * treatment categories, preferred days, time of day. All optional, all
 * skippable. On save, PATCH /api/waitlist/{email}/preferences writes to
 * Mongo AND syncs to the Klaviyo profile so Flow #08 (Live Opening Alert)
 * and the weekly curated-edit segments can filter on them.
 */

const REGIONS = [
  { key: "North Melbourne", label: "North" },
  { key: "South Melbourne", label: "South" },
  { key: "East Melbourne", label: "East" },
  { key: "West Melbourne", label: "West" },
  { key: "CBD", label: "CBD" },
];

const CATEGORIES = ["Hair", "Skin", "Brows + Lashes", "Body", "Nails", "Sauna", "Contrast Therapy", "Bathhouse", "Float Tank", "Infrared Sauna", "Massage"];
const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const TIMES = ["Morning", "Afternoon", "Evening"];

export default function Preferences() {
  const [params] = useSearchParams();
  const navigate = useNavigate();

  const [email, setEmail] = useState((params.get("email") || "").trim().toLowerCase());
  const [token] = useState((params.get("t") || "").trim());
  const [regions, setRegions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [days, setDays] = useState([]);
  const [times, setTimes] = useState([]);
  const [phone, setPhone] = useState("");
  const [emailOptIn, setEmailOptIn] = useState(true);
  const [liveOpeningAlerts, setLiveOpeningAlerts] = useState(true);
  const [smsOptIn, setSmsOptIn] = useState(false);
  const [loading, setLoading] = useState(!!email);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [linkError, setLinkError] = useState("");

  useEffect(() => {
    if (!email) { setLoading(false); return; }
    if (!token) { setLinkError("This preferences link is missing its security token. Please use the exact link from your welcome email."); setLoading(false); return; }
    let cancelled = false;
    (async () => {
      try {
        const entry = await getWaitlistEntry(email, token);
        if (cancelled) return;
        if (!entry) { setNotFound(true); setLoading(false); return; }
        setRegions(entry.preferred_regions || []);
        setCategories(entry.preferred_categories || []);
        setDays(entry.preferred_days || []);
        setTimes(entry.preferred_times || []);
        setPhone(entry.phone || "");
        setEmailOptIn(entry.email_opt_in !== false);
        setLiveOpeningAlerts(entry.live_opening_alerts !== false);
        setSmsOptIn(!!entry.sms_opt_in);
      } catch (e) {
        console.warn("Failed to load waitlist entry:", e);
        if (e?.response?.status === 401 || e?.response?.status === 403) {
          setLinkError("This preferences link has expired or is invalid. Request a fresh one from your welcome email.");
        } else if (e?.response?.status === 404) {
          setNotFound(true);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [email, token]);

  const toggle = (setter, list, v) =>
    setter(list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);

  const save = async () => {
    if (!email || !email.includes("@")) {
      toast.error("We need your email to save preferences.");
      return;
    }
    if (!token) {
      setLinkError("This preferences link is missing its access token. Open the link from your welcome email — or reply to that email and we'll send a fresh one.");
      return;
    }
    setSaving(true);
    try {
      await updateWaitlistPreferences(email, {
        preferred_regions: regions,
        preferred_categories: categories,
        preferred_days: days,
        preferred_times: times,
        phone: phone || null,
        email_opt_in: emailOptIn,
        live_opening_alerts: liveOpeningAlerts,
        sms_opt_in: smsOptIn,
      }, token);
      setSaved(true);
      setLinkError("");
      toast.success("Saved. You'll see better matches from day one.");
    } catch (e) {
      const status = e?.response?.status;
      if (status === 404) {
        toast.error("We couldn't find your waitlist entry — join first, then set preferences.");
        setNotFound(true);
      } else if (status === 401) {
        setLinkError(e?.response?.data?.detail || "This preferences link isn't valid. Reply to your welcome email and we'll send a fresh one.");
      } else {
        toast.error("Something didn't save. Try again in a sec.");
        console.error(e);
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="pt-16 min-h-screen bg-bone flex items-center justify-center" data-testid="prefs-loading">
        <p className="text-[10px] tracking-[0.24em] uppercase text-mocha">Loading your picks…</p>
      </div>
    );
  }

  return (
    <div className="pt-16 min-h-screen bg-bone" data-testid="prefs-page">
      <div className="max-w-[720px] mx-auto px-6 md:px-10 py-14 md:py-20">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-[10px] tracking-[0.24em] uppercase text-mocha hover:text-espresso transition-colors"
          data-testid="prefs-back"
        >
          <ArrowLeft size={12} strokeWidth={1.5} /> Back
        </Link>

        <header className="mt-8 md:mt-10 pb-8 border-b border-sand">
          <p className="text-[10px] tracking-[0.28em] uppercase text-mocha">
            {saved ? "Preferences saved" : "Your preferences"}
          </p>
          <h1 className="mt-4 font-serif text-4xl md:text-6xl leading-[1.02] tracking-tight text-ink">
            Make your alerts<br /><em className="text-espresso">feel personal.</em>
          </h1>
          <p className="mt-6 text-sm text-ink/60 leading-relaxed max-w-[56ch]">
            Two minutes now, and the salons + slots we send you are actually the ones you'd click on.
            Every field is optional — skip anything that doesn't matter to you.
          </p>
        </header>

        {!email && (
          <div className="mt-10">
            <label className="text-[10px] tracking-[0.28em] uppercase text-mocha block mb-3">Your email</label>
            <input
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-transparent border border-sand rounded-sm px-4 py-3 text-ink placeholder:text-ink/30 focus:outline-none focus:border-warm"
              data-testid="prefs-email-input"
            />
          </div>
        )}

        {notFound && email && (
          <div className="mt-10 p-6 border border-sand rounded-sm bg-white/40" data-testid="prefs-not-found">
            <p className="text-sm text-ink/75 leading-relaxed">
              We couldn't find <strong className="text-espresso">{email}</strong> on the FYLLR waitlist yet.
              Join first — it's 15 seconds — then come straight back and we'll save your picks.
            </p>
            <Link
              to="/?view=landing"
              className="mt-5 inline-flex items-center gap-2 text-[11px] tracking-[0.24em] uppercase text-espresso link-underline"
              data-testid="prefs-not-found-signup"
            >
              Join the waitlist <ArrowRight size={12} strokeWidth={1.5} />
            </Link>
          </div>
        )}

        {!notFound && (
          <div className="mt-10 space-y-12">
            {linkError && (
              <div
                className="p-5 border border-warm/40 rounded-sm bg-warm/10 text-sm text-espresso leading-relaxed"
                data-testid="prefs-link-error"
              >
                {linkError}
              </div>
            )}
            {!linkError && !token && email && (
              <div
                className="p-5 border border-sand rounded-sm bg-white/40 text-sm text-ink/70 leading-relaxed"
                data-testid="prefs-no-token"
              >
                Heads up: this link is missing its secure access token, so we can't save changes from here.
                Open the link from your FYLLR welcome email instead — that one has the token attached.
              </div>
            )}
            <Section label="Which side of Melbourne?" testid="prefs-regions">
              <div className="flex flex-wrap gap-2">
                {REGIONS.map((r) => (
                  <Chip
                    key={r.key}
                    active={regions.includes(r.key)}
                    onClick={() => toggle(setRegions, regions, r.key)}
                    testid={`prefs-region-${r.label.toLowerCase()}`}
                  >
                    {r.label}
                  </Chip>
                ))}
              </div>
            </Section>

            <Section label="What are you into?" testid="prefs-categories">
              <div className="flex flex-wrap gap-2">
                {CATEGORIES.map((c) => (
                  <Chip
                    key={c}
                    active={categories.includes(c)}
                    onClick={() => toggle(setCategories, categories, c)}
                    testid={`prefs-category-${c.toLowerCase().replace(/[\s+]+/g, "-")}`}
                  >
                    {c}
                  </Chip>
                ))}
              </div>
            </Section>

            <Section label="Best days for you?" testid="prefs-days">
              <div className="flex flex-wrap gap-2">
                {DAYS.map((d) => (
                  <Chip
                    key={d}
                    active={days.includes(d)}
                    onClick={() => toggle(setDays, days, d)}
                    testid={`prefs-day-${d.toLowerCase()}`}
                  >
                    {d}
                  </Chip>
                ))}
              </div>
            </Section>

            <Section label="Time of day?" testid="prefs-times">
              <div className="flex flex-wrap gap-2">
                {TIMES.map((t) => (
                  <Chip
                    key={t}
                    active={times.includes(t)}
                    onClick={() => toggle(setTimes, times, t)}
                    testid={`prefs-time-${t.toLowerCase()}`}
                  >
                    {t}
                  </Chip>
                ))}
              </div>
            </Section>

            <Section label="Contact + comms" testid="prefs-contact">
              <div className="space-y-4">
                <label className="flex items-start gap-3 text-sm text-ink/75 cursor-pointer" data-testid="prefs-live-alerts-row">
                  <input
                    type="checkbox"
                    checked={liveOpeningAlerts}
                    onChange={(e) => setLiveOpeningAlerts(e.target.checked)}
                    className="mt-1 accent-espresso"
                    data-testid="prefs-live-alerts"
                  />
                  <span><strong className="font-medium text-ink">Live opening alerts</strong><br />Email me when a newly released session matches my categories and preferred times.</span>
                </label>
                <input
                  type="tel"
                  placeholder="Phone (optional) — 0400 000 000"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="w-full bg-transparent border border-sand rounded-sm px-4 py-3 text-ink placeholder:text-ink/30 focus:outline-none focus:border-warm"
                  data-testid="prefs-phone"
                />
                <label className="flex items-start gap-3 text-sm text-ink/75 cursor-pointer" data-testid="prefs-email-optin-row">
                  <input
                    type="checkbox"
                    checked={emailOptIn}
                    onChange={(e) => setEmailOptIn(e.target.checked)}
                    className="mt-1 accent-espresso"
                    data-testid="prefs-email-optin"
                  />
                  <span>Email me a weekly curated edit.</span>
                </label>
                <label className="flex items-start gap-3 text-sm text-ink/75 cursor-pointer" data-testid="prefs-sms-optin-row">
                  <input
                    type="checkbox"
                    checked={smsOptIn}
                    onChange={(e) => setSmsOptIn(e.target.checked)}
                    className="mt-1 accent-espresso"
                    data-testid="prefs-sms-optin"
                  />
                  <span>Text me if something special pops up. Msg &amp; data rates may apply. Reply STOP to opt out.</span>
                </label>
              </div>
            </Section>

            <div className="pt-4 flex items-center gap-4 flex-wrap">
              <button
                type="button"
                onClick={save}
                disabled={saving}
                className="inline-flex items-center gap-3 rounded-full bg-espresso text-bone px-8 py-4 text-[11px] tracking-[0.26em] uppercase hover:scale-[1.03] transition-transform disabled:opacity-60 disabled:cursor-not-allowed"
                data-testid="prefs-save"
              >
                {saving ? "Saving…" : saved ? "Saved · Update again" : "Save my picks"}
                {saved ? <Check size={14} strokeWidth={1.8} /> : <ArrowRight size={14} strokeWidth={1.5} />}
              </button>
              <button
                type="button"
                onClick={() => navigate("/")}
                className="text-[11px] tracking-[0.26em] uppercase text-mocha hover:text-espresso"
                data-testid="prefs-skip"
              >
                Skip for now
              </button>
            </div>
          </div>
        )}

        <footer className="mt-16 pt-8 border-t border-sand text-[12px] text-ink/55 leading-relaxed">
          <p>
            Change or turn off any of this later. Questions?{" "}
            <a href="mailto:info@fyllr.app" className="text-espresso hover:underline">info@fyllr.app</a>.
          </p>
        </footer>
      </div>
    </div>
  );
}

function Section({ label, testid, children }) {
  return (
    <section data-testid={testid}>
      <span className="text-[10px] tracking-[0.28em] uppercase text-mocha block mb-4">{label}</span>
      {children}
    </section>
  );
}

function Chip({ active, onClick, testid, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      data-testid={testid}
      className={
        "inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-[13px] tracking-[0.02em] font-light transition-all border " +
        (active
          ? "bg-espresso text-bone border-espresso"
          : "bg-transparent text-ink/75 border-sand hover:border-warm hover:text-espresso")
      }
    >
      {children}
    </button>
  );
}
